import { DocketDisplayItemType } from '../../../common/types';
import { PaginatedDocketItems } from '../../layoutHelper';
import { Grid, GridCell } from './types';
import { headerOrder } from '../../../common/constants';

export const createCell = (
  colIndex: number,
  rowIndex: number,
  pageIndex: number,
  positionsPerPage: number,
): GridCell => ({
  items: [],
  isOccupied: false,
  isTall: false,
  position: pageIndex * positionsPerPage + colIndex * 2 + rowIndex + 1,
  rowPosition: rowIndex === 0 ? 'top' : 'bottom',
});

export const ensureGridExists = (
  grids: Grid[],
  pageIndex: number,
  colIndex: number,
  colsPerPage: number,
): void => {
  // Add missing pages all at once
  if (pageIndex >= grids.length) {
    const initialLength = grids.length;
    const newPagesCount = pageIndex - initialLength + 1;

    grids.length = pageIndex + 1;

    // Create new pages with direct assignments
    for (let i = 0; i < newPagesCount; i++) {
      const newPageIndex = initialLength + i;
      const columns = new Array(colsPerPage);

      for (let colIdx = 0; colIdx < colsPerPage; colIdx++) {
        columns[colIdx] = [
          createCell(colIdx, 0, newPageIndex, colsPerPage * 2),
          createCell(colIdx, 1, newPageIndex, colsPerPage * 2),
        ];
      }

      grids[newPageIndex] = {
        page: newPageIndex + 1,
        columns: columns,
      };
    }
  }

  // Handle columns for the specific page
  const grid = grids[pageIndex];
  if (colIndex >= grid.columns.length) {
    const initialColsLength = grid.columns.length;
    const newColsCount = colIndex - initialColsLength + 1;

    // Pre-allocate columns array
    grid.columns.length = colIndex + 1;

    // Direct assignments
    for (let i = 0; i < newColsCount; i++) {
      const newColIndex = initialColsLength + i;
      grid.columns[newColIndex] = [
        createCell(newColIndex, 0, pageIndex, colsPerPage * 2),
        createCell(newColIndex, 1, pageIndex, colsPerPage * 2),
      ];
    }
  }
};

export const finalizeGrid = (grids: Grid[]) => {
  grids.forEach(grid => {
    grid.columns.forEach(column => {
      if (
        !column[0].items.length &&
        column[1].items.length > 0 &&
        !column[1].isTall
      ) {
        column[0].isReserved = true;
        const hasBottomHeader = column[1].items.some(
          item => item.type === DocketDisplayItemType.DOCKET_HEADER,
        );
        if (hasBottomHeader) {
          column[0].isOccupied = true;
        }
      }

      column.forEach(row => {
        if (row.items.length > 0) {
          const headerItems = row.items.filter(item =>
            headerOrder.includes(item.type),
          );

          if (headerItems.length > 1) {
            headerItems.forEach(item => {
              const index = row.items.findIndex(i => i === item);
              if (index !== -1) {
                row.items.splice(index, 1);
              }
            });

            headerItems.sort(
              (a, b) =>
                headerOrder.indexOf(a.type) - headerOrder.indexOf(b.type),
            );

            [...headerItems].reverse().forEach(item => {
              row.items.unshift(item);
            });
          }
        }
      });
    });
  });
};

export const convertGridToOutput = (
  grids: Grid[],
  colsPerPage: number,
): PaginatedDocketItems[] => {
  const result: PaginatedDocketItems[] = [];

  for (let gridIndex = 0; gridIndex < grids.length; gridIndex++) {
    const grid = grids[gridIndex];
    const page: PaginatedDocketItems = {
      page: grid.page,
      columns: [],
    };

    for (let colIndex = 0; colIndex < grid.columns.length; colIndex++) {
      const column = grid.columns[colIndex];
      const allItems = [];

      if (column[0].items.length > 0) {
        allItems.push(...column[0].items);
      }

      if (column[1].items.length > 0) {
        allItems.push(...column[1].items);
      }

      if (allItems.length > 0) {
        page.columns.push({
          column: page.columns.length + 1,
          items: allItems,
        });
      }
    }

    if (page.columns.length > 0) {
      result.push(page);
    }
  }

  if (result.length > 1) {
    let i = 0;
    while (i < result.length - 1) {
      const currentPage = result[i];
      const nextPage = result[i + 1];

      if (
        currentPage.columns.length < colsPerPage &&
        nextPage.columns.length > 0
      ) {
        const maxToMove = Math.min(
          colsPerPage - currentPage.columns.length,
          nextPage.columns.length,
        );

        if (maxToMove > 0) {
          const columnsToMove = nextPage.columns.splice(0, maxToMove);

          for (let j = 0; j < columnsToMove.length; j++) {
            columnsToMove[j].column = currentPage.columns.length + j + 1;
          }
          currentPage.columns.push(...columnsToMove);

          for (let j = 0; j < nextPage.columns.length; j++) {
            nextPage.columns[j].column = j + 1;
          }

          if (nextPage.columns.length === 0) {
            result.splice(i + 1, 1);
            continue;
          }
        }
      }
      i++;
    }
  }

  for (let i = 0; i < result.length; i++) {
    result[i].page = i + 1;
  }

  return result;
};
